import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneOutgoing, Info, PhoneCall, AlertTriangle } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import CareLineEditor from '../components/CareLineEditor.jsx';
import { callsAPI, outboundAPI, contactsAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { formatDateTimeWithZone, formatPhone, formatDuration } from '../lib/format.js';

// OUTBOUND CALLS — the calls Alfred PLACES on this line, and the schedule that
// decides when he places them.
//
// Why this is its own page rather than a card in Settings: on a care line the daily
// question is "did he get called, and did anyone reach him" — an operational
// question, not a setting. Burying it under Settings is how a line that had never
// once dialled out looked healthy for a week (Gary's line, 07-24 → 07-31: eleven
// calls, every one of them INBOUND).
//
// So the schedule and the actual placed calls sit on one screen. The record below
// is the check on the schedule above: if the schedule says 10:00 and 15:00 daily and
// this list is empty, the line is not doing what the settings claim.

// The /calls list route takes no direction filter, so we pull a page and filter here.
// 100 is plenty for a care line (a couple of calls a day); a busy tenant would need
// server-side filtering, which is why the empty state says what it counted.
const PAGE = 100;

// E.164 — the same shape core's placeSchema enforces, checked here so a typo is a
// message under the field rather than a 400 after the button was pressed.
const E164 = /^\+[1-9]\d{6,14}$/;

export default function OutboundCalls() {
  const { tenant, selectedTenant, isOwner, user } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;
  // POST /calls/place is admin-only server-side (outbound.js) — don't offer a button
  // that 403s.
  // ⛔ THIS WAS STRICTER THAN THE SERVER, SO IT HID A BUTTON THAT WOULD HAVE WORKED.
  // POST /calls/place accepts `admin` OR `member` (outbound.js), and treats a platform
  // owner as privileged elsewhere in the same handler — it is `req.isOwner` that earns the
  // after-hours override. This gate allowed only `admin`, so a member was refused a call
  // the server would have placed, and Brian — the platform owner, over the whole process —
  // was told "only an account admin can place calls" on his own dashboard.
  //
  // The rule: a client gate may never be STRICTER than the server's, or it invents a
  // permission error out of nothing. Mirrored deliberately; if outbound.js widens, widen here.
  const canCall = ['admin', 'member'].includes(user?.role) || isOwner;

  const [calls, setCalls] = useState([]);
  const [scanned, setScanned] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Reported by the care-line card so an empty screen can explain itself instead of
  // just being empty.
  const [careStatus, setCareStatus] = useState({ loading: true, unavailable: false, hasLine: false });

  // ── CALL NOW ─────────────────────────────────────────────────────────────────
  // Places a real call immediately (POST /calls/place), outside the schedule. The
  // scheduled chain answers "did today's check happen"; this answers "ring him now",
  // which is what someone actually wants when they're worried and it's 11am.
  const [directory, setDirectory] = useState([]);
  const [callTo, setCallTo] = useState('');
  const [callOpening, setCallOpening] = useState('');
  const [placing, setPlacing] = useState(false);
  const [placeResult, setPlaceResult] = useState(null);
  const [placeError, setPlaceError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    callsAPI
      .list({ limit: PAGE })
      .then((r) => {
        if (cancelled) return;
        const rows = r?.data || [];
        setScanned(rows.length);
        setTotal(Number(r?.total) || rows.length);
        setCalls(rows.filter((c) => c.direction === 'outbound'));
        setError('');
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load calls.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shownTenant?.id]);

  // The assistant's contact directory, so "call now" is a name rather than a number
  // typed from memory. 404 (route not deployed / no contacts) just leaves it empty.
  useEffect(() => {
    let cancelled = false;
    contactsAPI.list('')
      .then((r) => { if (!cancelled) setDirectory((r?.data || []).filter((c) => c.phone)); })
      .catch(() => { /* directory is a convenience; the number field still works */ });
    return () => { cancelled = true; };
  }, [shownTenant?.id]);

  const noSchedule = !careStatus.loading && (careStatus.unavailable || !careStatus.hasLine);
  const toValid = E164.test(callTo.trim());

  const placeNow = async () => {
    const to = callTo.trim();
    if (!toValid) { setPlaceError('Enter the number as +1 and 10 digits, e.g. +18655183879.'); return; }
    // This dials a real person the moment it's pressed. Name who, and say it's
    // immediate — every other control on this screen only schedules.
    const who = directory.find((d) => d.phone === to)?.name;
    if (!window.confirm(
      `Call ${who ? `${who} (${formatPhone(to)})` : formatPhone(to)} right now?\n\n`
      + 'This places a real call immediately — it does not wait for the schedule.'
    )) return;
    setPlacing(true);
    setPlaceError('');
    setPlaceResult(null);
    try {
      const r = await outboundAPI.place({
        to,
        intent: 'adhoc',
        // ⛔ 8am-9pm is the floor, and a test call at 11pm is exactly when you need one.
        // The route already supports this and honours it ONLY for a platform owner
        // (req.isOwner, the server-side allowlist) — it is ignored for anyone else, so
        // sending it always is safe. It exists because Brian was blocked from ear-testing
        // his own line at 9:13pm: "Need to have an override button … this way the system is
        // not blocking when we need to do tests." Every use is logged and audited.
        ...(isOwner ? { afterHoursOverride: true } : {}),
        ...(callOpening.trim() ? { openingLine: callOpening.trim() } : {})
      });
      setPlaceResult((r?.data || r) || {});
      setCallOpening('');
    } catch (err) {
      // 403 = not an admin, 429 = the originate rate limit, 400 = core refused the
      // number or had no line to call from. Each is a different fix, so don't
      // collapse them into "call failed".
      setPlaceError(
        err.status === 403 ? 'Only an account admin can place calls.'
          : err.status === 429 ? 'Too many calls placed in the last minute — wait and try again.'
            : (err.body?.message || err.message || 'The call could not be placed.')
      );
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Outbound calls"
        subtitle="The calls Alfred places on this line — the schedule that decides when, and the record of what actually went out."
      />

      {/* ── CALL NOW ─────────────────────────────────────────────────────────────
          First on the page, above the schedule: the schedule answers "did today's
          check happen", this answers "ring him now", and the second question is the
          urgent one. Admin-only, matching the server. */}
      {canCall && (
        <section className="card mb-8">
          <header className="mb-3 flex items-center gap-2">
            <PhoneCall size={18} className="text-alfred-gold" />
            <h2 className="text-xl text-stone-100">Call now</h2>
          </header>
          <p className="mb-4 text-sm text-stone-400">
            Places a call immediately, outside the schedule. Alfred rings the number and talks as
            he normally would on this line.
          </p>

          <div className="flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[240px]">
              <span className="field-label">Number to call</span>
              <input
                className="field-input"
                placeholder="+18655183879"
                value={callTo}
                onChange={(e) => { setCallTo(e.target.value); setPlaceError(''); }}
                list="outbound-directory"
              />
              {/* The directory drives a datalist rather than a <select>, so a number
                  that isn't in it can still be typed — the common case is a number
                  someone reads out over the phone. */}
              <datalist id="outbound-directory">
                {directory.map((d) => (
                  <option key={d.id} value={d.phone}>{d.name}{d.category ? ` — ${d.category}` : ''}</option>
                ))}
              </datalist>
              {!!callTo && !toValid && (
                <span className="mt-1 block text-xs text-amber-300">
                  Use E.164 — a plus, the country code, then the number: +18655183879.
                </span>
              )}
            </label>
            <button
              type="button"
              className="btn-primary"
              onClick={placeNow}
              disabled={placing || !callTo.trim()}
            >
              <PhoneCall size={14} /> {placing ? 'Calling…' : 'Call now'}
            </button>
          </div>

          <label className="mt-3 block">
            <span className="field-label">Opening line (optional)</span>
            <input
              className="field-input"
              placeholder="e.g. Hi Gary — just checking in on this morning's drink."
              maxLength={300}
              value={callOpening}
              onChange={(e) => setCallOpening(e.target.value)}
            />
            <span className="mt-1 block text-xs text-stone-500">
              Spoken as the first thing on the call. Leave blank for Alfred&rsquo;s normal opening.
            </span>
          </label>

          {directory.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-stone-500">Directory:</span>
              {directory.slice(0, 6).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => { setCallTo(d.phone); setPlaceError(''); }}
                  className="rounded-full border border-stone-700 bg-stone-800/40 px-2.5 py-1 text-xs text-stone-300 hover:bg-stone-800"
                >
                  {d.name}
                </button>
              ))}
              <Link to="/contacts" className="text-xs text-stone-500 underline hover:text-stone-300">
                Manage contacts
              </Link>
            </div>
          )}

          {placeError && (
            <p className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              {placeError}
            </p>
          )}
          {placeResult && (
            <p className="mt-3 text-sm text-emerald-300">
              Calling {formatPhone(callTo.trim())} now
              {placeResult.status ? ` — ${placeResult.status}` : ''}.
              {placeResult.id && (
                <> <Link to={`/calls/${placeResult.id}`} className="underline">Open the call</Link> to follow it.</>
              )}
            </p>
          )}
        </section>
      )}

      <div className="mb-8">
        <CareLineEditor
          tenantId={shownTenant?.id}
          tenantName={shownTenant?.companyName}
          onStatus={setCareStatus}
        />

        {/* The card hides itself when there's nothing to show. On a nav destination
            that would be a blank page, so say plainly what the state is — and DON'T
            guess which of the two causes it is: core returns the same 404 for "this
            route isn't deployed" and "this tenant has no care line". */}
        {noSchedule && (
          <section className="card">
            <div className="flex items-start gap-3">
              <Info size={18} className="mt-0.5 shrink-0 text-stone-500" />
              <div>
                <h2 className="text-lg text-stone-100">No call schedule on this line</h2>
                <p className="mt-1 text-sm text-stone-400">
                  Nothing is scheduled, so <span className="text-stone-200">Alfred is not placing any
                  calls</span> on this account. Either no schedule has been set up for it, or the
                  scheduling service isn&rsquo;t live yet — the dashboard can&rsquo;t tell the two apart from
                  here. Any call in the record below was placed some other way.
                </p>
              </div>
            </div>
          </section>
        )}
      </div>

      <section className="card">
        <header className="mb-4 flex items-center gap-2">
          <PhoneOutgoing size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Calls Alfred placed</h2>
        </header>

        {error && (
          <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
        )}

        {loading ? (
          <p className="text-sm text-stone-500">Loading…</p>
        ) : error ? (
          // ⛔ A failed fetch must NOT fall through to the empty state. "Alfred has
          // never placed a call on this line" is a definitive claim about a care
          // line, and a three-second 502 is not evidence for it.
          <p className="text-sm text-stone-500">
            The call record couldn&rsquo;t be loaded, so this list is not showing what did or
            didn&rsquo;t go out. Refresh to try again.
          </p>
        ) : calls.length === 0 ? (
          <div className="text-sm text-stone-400">
            {/* Scoped to what was actually examined. The unscoped version read
                "Alfred has never placed a call on this line" off a single page. */}
            <p className="text-stone-200">
              {scanned >= total
                ? 'Alfred has never placed a call on this line.'
                : 'No outbound call in this line’s recent history.'}
            </p>
            <p className="mt-1 text-stone-500">
              Nothing outbound in the {scanned >= total
                ? `${total} ${total === 1 ? 'call' : 'calls'} on this account`
                : `${scanned} most recent of ${total} calls on this account`}.
              If a schedule is set above, this is the gap to chase — the schedule exists but no call
              is leaving.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-stone-800">
            {calls.map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
                <div className="min-w-0">
                  <Link to={`/calls/${c.id}`} className="text-stone-200 hover:text-alfred-gold">
                    {formatPhone(c.toNumber)}
                  </Link>
                  <span className="text-stone-500"> — {formatDateTimeWithZone(c.startedAt)}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-stone-500">
                  {/* answeredAt is the honest signal here: this line writes
                      durationSeconds as 0, so a duration of 0 means "not recorded",
                      never "nobody talked". */}
                  <span className={c.answeredAt ? 'text-emerald-300' : 'text-stone-500'}>
                    {c.answeredAt ? 'Answered' : 'No answer recorded'}
                  </span>
                  {c.durationSeconds > 0 && <span>{formatDuration(c.durationSeconds)}</span>}
                  <span className="capitalize">{c.status || '—'}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
